import { useEffect } from "react";

import { clearStrandedBodyPointerEvents } from "@/lib/pointerEventsGuard";

/**
 * App-wide safety net for the Radix "stranded body pointer-events" freeze.
 *
 * Mount this once near the app root. It watches the document for the DOM changes
 * that bracket a dialog/menu close — body `style` mutations and node removals —
 * and recovers from the stranded state by clearing a leftover
 * `pointer-events: none` on <body> once no Radix modal layer remains open.
 *
 * Why a broad subtree observer is fine: {@link clearStrandedBodyPointerEvents}
 * early-returns unless <body> is currently frozen, so the work per mutation is a
 * single string comparison except during the brief window a dialog is actually
 * open. This deterministically defeats the intermittent, timing-/browser-
 * dependent race regardless of which delete flow triggers it.
 */
export function usePointerEventsGuard(): void {
  useEffect(() => {
    const guard = () => clearStrandedBodyPointerEvents();

    const observer = new MutationObserver(guard);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
      childList: true,
      subtree: true,
    });

    // Recover immediately if we mounted into an already-stranded state.
    guard();

    return () => observer.disconnect();
  }, []);
}
